import React from 'react'
import { Row, Column, Typography, FlexBox, H2 } from '@vp/swan'
import { useAppContext } from '../state/AppContext'
import { getGalleryUrl } from '../utils/gallery/gallery'
import { Gallery } from '../types/Gallery'
import { trackClickEvent, trackTemplateTileClickEvent } from '../utils/tracking/tracking'
import { TemplateCollectionGridItems, TemplateCollectionType } from '../types/TemplateConfiguration'
import { DisabledAltText, Translated } from '../types/Global'
import { DebugPageSections } from './debugComponents/DebugPageSections'
import { SkipToNextSectionLink } from './SkipToNextSectionLink'
import { stylesheet } from '../styles/TemplateCollectionGrid.scss'
import { useStyles } from '@vp/ubik-context'
import { LinkWrapper } from './LinkWrapper'
import CloudinaryUtil from '../utils/cloudinaryUtil/cloudinaryUtil'
import { CategoryTile } from '@vp/ep-tiles'
import { stylesheet as categoryTileStylesheet } from '@vp/ep-tiles/category-tile.css'
import { CLOUDINARY_TRANSFORM_TOKENS } from './DesignTemplate/constants'

interface CategoryGridWrapperProps {
  configuration: TemplateCollectionType
  galleries?: Gallery[]
  nextSectionId?: string
  nextSectionTitle?: Translated
  index?: number
}

const pageZone = 'Template Collection Grid'
const CategoryGridWrapper = (props: CategoryGridWrapperProps) => {
  useStyles(stylesheet)
  useStyles(categoryTileStylesheet)

  const { configuration, galleries, nextSectionTitle, nextSectionId } = props
  const { title, subtitle, cta, items } = configuration
  const { locale, sidebarEnabled, merchandisingCategoryId } = useAppContext()

  const ctaElement = cta ? (
    <FlexBox justifyContent="flex-end" alignItems="flex-end">
      <Typography fontSize="standard">
        <LinkWrapper
          href={cta.targetUrl}
          skin="cta"
          data-section={pageZone}
          data-translation={merchandisingCategoryId}
          onClick={() =>
            trackClickEvent({
              categoryId: merchandisingCategoryId,
              destinationUrl: cta.targetUrl,
              ctaValue: 'CTA',
              pageZone
            })
          }
        >
          {cta.text}
        </LinkWrapper>
      </Typography>
    </FlexBox>
  ) : undefined

  const templateCollectionGridTiles = items
    .map((item: TemplateCollectionGridItems, tileIndex: number) => {
      if (!item || !item.link) {
        return null
      }
      const { cloudinaryImage, text, actionId, actionFilters, targetUrl } = item.link
      if (!cloudinaryImage?.cloudinaryUrl || !targetUrl) {
        return null
      }
      const transformedImageURL = CloudinaryUtil.applyImageTransformation(
        cloudinaryImage.cloudinaryUrl,
        CLOUDINARY_TRANSFORM_TOKENS
      )
      const url: string =
        getGalleryUrl({
          ...(galleries && { galleries }),
          ...(actionFilters && { actionFilters }),
          actionId,
          locale
        }) || targetUrl
      const trackingIndex = tileIndex + 1

      return (
        <CategoryTile
          categoryId={actionId}
          categoryTileTitle={text}
          categoryTileImage={{
            src: transformedImageURL,
            altTextOverride: DisabledAltText
          }}
          categoryTileUrl={url}
          trackOnClick={() =>
            trackTemplateTileClickEvent({
              categoryId: merchandisingCategoryId,
              destinationUrl: url,
              pageZone,
              ctaValue: `Tile ${trackingIndex}`,
              index: trackingIndex,
              itemsCount: items?.length,
              sectionTypeCount: configuration.sectionTypeCount ?? 1
            })
          }
          key={actionId}
        />
      )
    })
    .filter((slide) => !!slide)

  if (!templateCollectionGridTiles.length) {
    return null
  }

  return (
    <>
      {title && title.value && (
        <Row mb="5">
          <Column span={10}>
            <H2 fontSize="x2large" mb={0}>
              {title.value}
            </H2>
          </Column>
        </Row>
      )}
      <SkipToNextSectionLink
        {...(nextSectionId && { nextSectionId })}
        {...(nextSectionTitle && { nextSectionTitle })}
      />
      {((subtitle && subtitle.value) || ctaElement) && (
        <Row>
          <Column span={sidebarEnabled ? 7 : 5}>
            {subtitle && subtitle.value && <Typography>{subtitle.value}</Typography>}
          </Column>
          <Column span={sidebarEnabled ? 5 : 7}>{ctaElement}</Column>
        </Row>
      )}
      <Row marginTop={7}>
        {templateCollectionGridTiles.map((tile, index) => {
          return (
            <Column span={2} spanXs={4} key={index} marginBottom={5}>
              {tile}
            </Column>
          )
        })}
      </Row>
      <DebugPageSections rawSectionData={configuration} />
    </>
  )
}

export default CategoryGridWrapper
